import store from '@/store';
import { cloneDeep } from 'lodash';
import utility from '@/shared/utility';
import { translate } from '@/translation/native_translate';
import HighlightingLabelJS from '@/objects/HighlightingLabel';

/**
 * Sort item def names alphabetically
 * @param {object} itemDefs Item defs object, e.g.
 *                          {
 *                            Size: {
 *                              0: 'Small',
 *                              1: 'Big',
 *                            },
 *                            Color: {
 *                              0: 'Red',
 *                              1: 'Green',
 *                            }
 *                          }
 * @returns Sorted item def names, e.g. ['Color', 'Size']
 */
function getSortedItemDefNames(itemDefs) {
  if (itemDefs == null) {
    return [];
  }

  const defNames = Object.keys(itemDefs).sort((a, b) => {
    const defA = a.toUpperCase();
    const defB = b.toUpperCase();

    if (defA < defB) {
      return -1;
    }
    if (defA > defB) {
      return 1;
    }

    return 0;
  });

  return defNames;
}

/**
 * Sort each item def options in two cases:
 *   - Sort by price if only one item def is available
 *   - Sort alphabetically if multiple item defs are available
 * @param {object} product Product details object
 * @param {string} itemDefName Item def name, e.g. 'Size' , whose values will be sorted
 * @returns Sorted values for requested item def, e.g. ['Big', 'Small']
 */
function getSortedItemDefValues(product, itemDefName) {
  let defValues = [];

  if (Object.keys(product.ItemDefs).length === 0) {
    return defValues; // Return an empty array if there are no ItemDefs
  }

  product.Items.forEach((item) => {
    if (item.ItemDef != null && item.ItemDef[itemDefName] != null) {
      const value = item.ItemDef[itemDefName];
      const indexInResult = defValues.indexOf(value);
      const indexInDefs = Object.values(product.ItemDefs[itemDefName]).indexOf(value);
      
      if (indexInResult < 0 && indexInDefs >= 0) {
        defValues.push(value);
      }
    }
  });
  // Sort alphabetically
  defValues.sort((a, b) => {
    // Sort alphabetically
    return a.localeCompare(b);
  });

  return defValues;
}

function removeDefaultItemFromItems(items) {
  if (items == null || items.length === 0) {
    return;
  }

  const defaultItemIndex = items.findIndex(
    (item) => Object.keys(item.ItemDef).length === 0
  );

  if (defaultItemIndex >= 0) {
    items.splice(defaultItemIndex, 1);
  }
}

function removeDefaultItem(product) {
  removeDefaultItemFromItems(product.Items);
}

function getItemById(product, itemId) {
  if (product == null || product.Items == null || product.Items.length === 0) {
    // Empty product
    return {};
  }

  if (itemId == null || itemId.trim() === '') {
    // Invalid item id
    return {};
  }

  const itemFound = product.Items.find((item) => item.Id === itemId);

  if (itemFound == null) {
    return {};
  }

  return itemFound;
}

function getItemWithLowestPrice(product) {
  if (product == null) {
    return null;
  }

  let items = null;

  if (product.Items != null) {
    items = cloneDeep(product.Items);
  } else if (product.Product != null && product.Product.Items != null) {
    items = cloneDeep(product.Product.Items);
  }

  if (items == null || items.length === 0) {
    // Invalid data. No items available
    return null;
  }

  if (items.length > 1) {
    removeDefaultItemFromItems(items);
  }

  items.sort((a, b) => a.Price > b.Price);
  return items[0];
}

function hasItems(product) {
  return product != null && product.Items != null && product.Items.length > 0;
}

function hasOnlyOneItem(product) {
  return hasItems(product) && product.Items.length === 1;
}

function getItemByDefs(product, defs) {
  if (!hasItems(product)) {
    // Empty product
    return {};
  }

  if (product.Items.length === 1) {
    // Only one item (no variations)
    return product.Items[0];
  }

  // Has variations, return the one matches input item defs
  if (
    defs == null ||
    Object.keys(defs).length === 0 ||
    Object.keys(defs).length !== Object.keys(product.ItemDefs).length
  ) {
    // No variations selected or selection not completed yet
    return {};
  }

  // Selection of variations completed, get matched item
  const itemFound = product.Items.find((item) => {
    let variationMatch = true;

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(defs)) {
      if (item.ItemDef[key] == null || item.ItemDef[key] !== value) {
        // Not match, stop looping
        variationMatch = false;
        break;
      }
    }

    return variationMatch;
  });

  if (itemFound == null) {
    return {};
  }

  return itemFound;
}

function isWeightedProduct(product) {
  if (
    product != null &&
    product.Product != null &&
    product.Product.Type === 'Weighted'
  ) {
    return true;
  }

  if (
    product != null &&
    (product.Type === 'Weighted' || product.ProductType === 'Weighted')
  ) {
    return true;
  }

  return false;
}

function getTotalPriceInclVAT(product) {
  let price = null;

  if (product != null) {
    if (isWeightedProduct(product)) {
      // Return total / starting price for Weighted product
      price =
        (product.Quantity / product.Product.WeightedInfo.StepSize) *
        product.Product.WeightedInfo.PricePerStep;
    } else if (product.ItemId == null || product.ItemId === '') {
      // Return total starting price
      price = product.Product.Price * product.Quantity;
    } else {
      // Return total price for selected item
      price = product.Item.Price * product.Quantity;
    }
  }

  return price;
}

function getTotalPriceExclVAT(product) {
  let price = null;

  if (product != null) {
    if (isWeightedProduct(product)) {
      // Return total / starting price for Weighted product
      price =
        (product.Quantity / product.Product.WeightedInfo.StepSize) *
        product.Product.WeightedInfo.PricePerStep;

      price = utility.getPriceExclVAT(price, product.Product.VatPercentage);
    } else if (product.ItemId == null || product.ItemId === '') {
      // Return total starting price
      price = product.Product.PriceExclVat * product.Quantity;
    } else {
      // Return total price for selected item
      price = product.Item.PriceExclVat * product.Quantity;
    }
  }

  return price;
}

function getTotalPrice(product) {
  return getTotalPriceInclVAT(product);
}

function getTotalRetailPriceInclVAT(product) {
  if (isWeightedProduct(product)) {
    if (
      product.Product.Discount &&
      product.Product.WeightedInfo.RetailPricePerStep != null &&
      product.Product.WeightedInfo.RetailPricePerStep !==
        product.Product.WeightedInfo.PricePerStep
    ) {
      // Weighted product with discounts
      const amount = product.Quantity / product.Product.WeightedInfo.StepSize;
      return amount * product.Product.WeightedInfo.RetailPricePerStep;
    }

    // Weighted product without discounts
    return null;
  }

  if (
    product.Item != null &&
    product.Item.Discount &&
    product.Item.RetailPrice !== product.Item.Price
  ) {
    // Standard / Rental product with discounts
    return product.Item.RetailPrice * product.Quantity;
  }

  // Standard / Rental product without discounts
  return null;
}

function getTotalRetailPriceExclVAT(product) {
  const priceInclVAT = getTotalRetailPriceInclVAT(product);
  return utility.getPriceExclVAT(priceInclVAT, product.Product.VatPercentage);
}

function getTotalRetailPrice(product) {
  return getTotalRetailPriceInclVAT(product);
}

/**
 * Get vat info text
 * @param {number} vatPercentage vat percentage, e.g. 8
 * @returns vat text which shows the vat info, e.g. incl. 8.0% VAT
 */
function getVATText(vatPercentage) {
  const vat = Number(vatPercentage);
  let vatPercent = 0.0;
  if (!Number.isNaN(vat)) {
    vatPercent = vat.toFixed(1);
  }
  const vatPercentStr = `${vatPercent}%`;

  const vatTextTemp = translate('INCL_VAT_TEMPLATE');
  const vatText = `${utility.sprintf(vatTextTemp, vatPercentStr)}`;

  return vatText;
}

/**
 * Check if the product or selected item has a discount
 * @param {object} product either Product object or Product selection object
 * @returns true or false for Discount
 */
function hasGeneralDiscount(product) {
  if (product == null) {
    return false;
  }

  if (product.Item != null && Object.keys(product.Item).length === 0) {
    // Has selected item, discount should be Item dependant.
    // Could be used in product selection visualization
    return product.Item.Discount;
  }

  // No selection, only check product info. Could be used in product list visualization
  if (product.Product != null) {
    return product.Product.Discount;
  }

  return product.Discount;
}

/**
 * Get the discount percentage (personal discount) for partner's product
 * @param {object} product product info
 * @returns discount percentage for partner's product
 */
function personalDiscount(product) {
  let partnerId = '';
  if (product.Product != null) {
    partnerId = product.Product.PartnerId;
  } else {
    partnerId = product.PartnerId;
  }

  if (partnerId == null) {
    return 0;
  }

  const partnerUserRelations = store.getters['auth/partnerUserRelations'];
  const relations = partnerUserRelations[partnerId];
  if (
    relations != null &&
    relations.DiscountPercentage != null &&
    relations.DiscountPercentage > 0
  ) {
    return relations.DiscountPercentage;
  }
  return 0;
}

/**
 * Check if the product or selected item is on discount
 * (either general discount or personal discount)
 * @param {object} product either Product object or Product selection object
 * @returns true or false for Discount
 */
function isOnDiscount(product) {
  // General discount check
  if (hasGeneralDiscount(product)) {
    return true;
  }

  // Personal discount check
  if (personalDiscount(product) > 0) {
    return true;
  }
  return false;
}

/**
 * Decides whether a personal discount should be used or not
 * @param {object} product either Product object or Product selection object
 * @returns true if the personal discount should be applied to the product, otherwise false
 */
function applyPersonalDiscount(product) {
  if (personalDiscount(product) > 0 && !hasGeneralDiscount(product)) {
    return true;
  }
  return false;
}

function getUnitPriceForWeightedProduct(product) {
  if (!isWeightedProduct(product)) {
    return null;
  }

  const { isProfUser } = store.getters;

  let pricePerStep = null;
  let vatPercentage = null;

  if (product == null) {
    return null;
  }

  if (product.Product != null) {
    pricePerStep = product.Product.WeightedInfo.PricePerStep;
    vatPercentage = product.Product.VatPercentage;
  } else {
    pricePerStep = product.WeightedInfo.PricePerStep;
    vatPercentage = product.VatPercentage;
  }

  if (pricePerStep == null) {
    return null;
  }

  if (isProfUser) {
    return utility.getPriceExclVAT(pricePerStep, vatPercentage);
  }

  return pricePerStep;
}

function getUnitPriceForStandardProduct(product) {
  if (product == null) {
    return null;
  }

  const { isProfUser } = store.getters;

  let item = null;
  if (product.Item != null) {
    item = product.Item;
  } else {
    item = getItemWithLowestPrice(product);
  }

  let price = null;

  if (isProfUser) {
    if (item == null) {
      // Return starting price
      if (product.Product == null) {
        price = product.PriceExclVat;
      } else {
        price = product.Product.PriceExclVat;
      }
    } else {
      // Return price for selected item
      price = item.PriceExclVat;
    }
  } else if (item == null) {
    if (product.Product == null) {
      price = product.Price;
    } else {
      price = product.Product.Price;
    }
  } else {
    price = item.Price;
  }

  return price;
}

function getRetailUnitPriceForWeightedProduct(product) {
  if (!isWeightedProduct(product)) {
    return null;
  }

  const { isProfUser } = store.getters;

  let retailPricePerStep = null;
  let vatPercentage = null;

  if (product == null) {
    return null;
  }

  if (product.Product != null) {
    retailPricePerStep = product.Product.WeightedInfo.RetailPricePerStep;
    vatPercentage = product.Product.VatPercentage;
  } else {
    retailPricePerStep = product.WeightedInfo.RetailPricePerStep;
    vatPercentage = product.VatPercentage;
  }

  if (retailPricePerStep == null) {
    return null;
  }

  if (isProfUser) {
    return utility.getPriceExclVAT(retailPricePerStep, vatPercentage);
  }

  return retailPricePerStep;
}

function getRetailUnitPriceForStandardProduct(product) {
  if (product == null) {
    return null;
  }

  const { isProfUser } = store.getters;

  let item = null;
  if (product.Item != null) {
    item = product.Item;
  } else {
    item = getItemWithLowestPrice(product);
  }

  let price = null;

  if (isProfUser) {
    let vatPercentage = null;
    if (product.Product == null) {
      vatPercentage = product.VatPercentage;
    } else {
      vatPercentage = product.Product.VatPercentage;
    }

    if (item == null) {
      // Return starting price
      if (product.Product == null) {
        price = product.RetailPrice;
      } else {
        price = product.Product.RetailPrice;
      }
    } else {
      // Return price for selected item
      price = item.RetailPrice;
    }
    price = utility.getPriceExclVAT(price, vatPercentage);
  } else if (item == null) {
    if (product.Product == null) {
      price = product.RetailPrice;
    } else {
      price = product.Product.RetailPrice;
    }
  } else {
    price = item.RetailPrice;
  }

  return price;
}

function getUnitPrice(product) {
  if (isWeightedProduct(product)) {
    return getUnitPriceForWeightedProduct(product);
  }
  return getUnitPriceForStandardProduct(product);
}

function getOverviewPrice(product) {
  if (!isWeightedProduct(product)) {
    return null;
  }

  const { isProfUser } = store.getters;

  let overviewPrice = null;
  let vatPercentage = null;

  if (product == null) return null;

  if (product.Product != null) {
    overviewPrice = product.Product.WeightedInfo.OverviewPrice;
    vatPercentage = product.Product.VatPercentage;
  } else {
    overviewPrice = product.WeightedInfo.OverviewPrice;
    vatPercentage = product.VatPercentage;
  }

  if (overviewPrice == null) {
    return null;
  }

  if (isProfUser) {
    return utility.getPriceExclVAT(overviewPrice, vatPercentage);
  }

  return overviewPrice;
}

function getRetailOverviewPrice(product) {
  if (!isWeightedProduct(product)) {
    return null;
  }

  const { isProfUser } = store.getters;

  let overviewPrice = null;
  let vatPercentage = null;

  if (product == null) return null;

  if (product.Product != null) {
    overviewPrice = product.Product.WeightedInfo.OverviewRetailPrice;
    vatPercentage = product.Product.VatPercentage;
  } else {
    overviewPrice = product.WeightedInfo.OverviewRetailPrice;
    vatPercentage = product.VatPercentage;
  }

  if (overviewPrice == null) {
    return null;
  }

  if (isProfUser) {
    return utility.getPriceExclVAT(overviewPrice, vatPercentage);
  }

  return overviewPrice;
}

function getRetailUnitPrice(product) {
  if (isWeightedProduct(product)) {
    return getRetailUnitPriceForWeightedProduct(product);
  }
  return getRetailUnitPriceForStandardProduct(product);
}

function getDeposit(product) {
  let deposit = 0.0;
  if(product.Product.DepositValue > 0) {
    deposit = product.Product.DepositValue * product.Quantity;
  }
  return deposit;
}

function getDiscount(product) {
  let price = 0;
  const { isProfUser } = store.getters;

  let productRetailPrice = 0.0;
  let productPrice = 0.0;

  if (isProfUser) {
    productRetailPrice = getTotalRetailPriceExclVAT(product);
    if (productRetailPrice == null || productRetailPrice == 0) {
      productRetailPrice = getTotalPriceExclVAT(product);
    }

    productPrice = getTotalPriceExclVAT(product);
  } else {
    productRetailPrice = getTotalRetailPriceInclVAT(product);
    if (productRetailPrice == null || productRetailPrice == 0) {
      productRetailPrice = getTotalPriceInclVAT(product);
    }

    productPrice = getTotalPriceInclVAT(product);
  }

  if (applyPersonalDiscount(product)) {
    productPrice = utility.getDiscountPrice(
      productPrice,
      personalDiscount(product)
    );
  }
  if (productRetailPrice > productPrice) {
    price = productPrice - productRetailPrice;
  }
  return price;
}

/**
 * Check if a product / item still has capacity or not for order
 * @param {object} product
 * @param {object} productItem
 * @returns
 */
function hasCapacity(product, productItem = null) {
  const capacityManagedValue = product.CapacityManaged;

  let capacity = product.Capacity;
  if (productItem != null && Object.keys(productItem).length > 0) {
    capacity = productItem.Capacity;
  }

  let minValue = product.MinimumQuantity;
  if (isWeightedProduct(product)) {
    minValue = product.WeightedInfo.Minimum;
  }

  const capacityNotEnough = capacity < minValue;
  const noCapacity = capacity === 0;

  let hasCapacity = true;

  if (capacityManagedValue && (capacityNotEnough || noCapacity)) {
    hasCapacity = false;
  }

  return hasCapacity;
}

function getCapacity(product, productItem = null) {
  const capacityManagedValue = product.CapacityManaged;

  let capacity = product.Capacity;
  if (productItem != null && Object.keys(productItem).length > 0) {
    capacity = productItem.Capacity;
  }

  let minValue = product.MinimumQuantity;
  if (isWeightedProduct(product)) {
    minValue = product.WeightedInfo.Minimum;
  }

  const capacityNotEnough = capacity < minValue;

  if (capacityManagedValue && capacityNotEnough) {
    capacity = 0;
  }

  return capacity;
}

function getHighlightingLabelStyle(product) {
  if (product == null) {
    return '';
  }
  const labelObj = new HighlightingLabelJS(product.HighlightingLabel);
  return labelObj.getStyle();
}

/**
 * Check if a product has at least one product option or not
 * @param {object} product product object
 * @returns true if at least one option available; Otherwise false
 */
function hasOptions(product) {
  if (product.OptionLists == null || product.OptionLists.length === 0) {
    return false;
  }

  let hasOptionItems = false;
  for (const list of product.OptionLists) {
    const allGroupsEmpty = list.OptionGroups.every(
      (group) => group.Options.length === 0
    );
    if (!allGroupsEmpty) {
      hasOptionItems = true;
      break;
    }
  }
  return hasOptionItems;
}

/**
 * Get summary string for selected options
 * @param {array} optionSelections list of selected options
 */
function toOptionSummary(optionSelections) {
  if (optionSelections == null || !Array.isArray(optionSelections)) {
    return '';
  }

  let summaryStr = '';

  for (let index = 0; index < optionSelections.length; index++) {
    const option = optionSelections[index];
    if (option.Quantity <= 0) {
      continue;
    }

    summaryStr += `${option.Quantity} x ${option.OptionName}`;

    if (index < optionSelections.length - 1) {
      summaryStr += ', ';
    }
  }

  return summaryStr;
}

function isSameOptionSelections(selectionsA, selectionsB) {
  if (selectionsA == null || selectionsB == null) {
    // Null selections
    return false;
  }

  if (!Array.isArray(selectionsA) || !Array.isArray(selectionsB)) {
    // Selections are not array
    return false;
  }

  if (selectionsA.length === 0 || selectionsB.length === 0) {
    // Empty selections
    return false;
  }

  if (selectionsA.length !== selectionsB.length) {
    // Number of selections not the same
    return false;
  }

  const aAllFoundInB = selectionsA.every((optionInA) => {
    const index = selectionsB.findIndex(
      (optionInB) =>
        optionInA.OptionId === optionInB.OptionId &&
        optionInA.Quantity === optionInB.Quantity
    );
    return index >= 0;
  });

  const bAllFoundInA = selectionsB.every((optionInB) => {
    const index = selectionsA.findIndex(
      (optionInA) =>
        optionInA.OptionId === optionInB.OptionId &&
        optionInA.Quantity === optionInB.Quantity
    );
    return index >= 0;
  });

  return aAllFoundInB && bAllFoundInA;
}

function getMinimumQuantity(product, productItem) {
  let minValue = Math.max(1, product.MinimumQuantity);

  if (!hasCapacity(product, productItem)) {
    minValue = 0;
  } else if (isWeightedProduct(product)) {
    minValue = Math.max(1, product.WeightedInfo.Minimum);
  }

  return minValue;
}

function getMaximumQuantity(product, productItem) {
  let maxValue = product.MaximumQuantity;

  if (!hasCapacity(product, productItem)) {
    maxValue = 0;
  } else {
    let capacity = product.Capacity;
    let capacityManaged = product.CapacityManaged;

    if (productItem != null && Object.keys(productItem).length > 0) {
      capacity = productItem.Capacity;
    }

    if (capacityManaged && maxValue !== -1) {
      // Has both maximum and capacity limit
      maxValue = Math.min(capacity, maxValue);
    } else if (capacityManaged) {
      // Has no maximum limit, but capacity limit
      maxValue = capacity;
    }
  }

  return maxValue;
}

function quantityDecreasable(product, productItem, quantity) {
  if (
    product == null ||
    productItem == null ||
    Object.keys(product).length === 0 ||
    Object.keys(productItem).length === 0
  ) {
    return false;
  }

  const minValue = getMinimumQuantity(product, productItem);

  return quantity > minValue;
}

function quantityIncreasable(product, productItem, quantity) {
  if (
    product == null ||
    productItem == null ||
    Object.keys(product).length === 0 ||
    Object.keys(productItem).length === 0
  ) {
    return false;
  }

  const maxValue = getMaximumQuantity(product, productItem);

  if (maxValue < 0) {
    return true;
  }

  return quantity < maxValue;
}

/**
 * Get corresponding quantity upon product item change, e.g. after item def is changed
 * @param {object} product
 * @param {object} productItem
 * @param {number} currentQuantity
 * @returns
 */
function getQuantity(product, productItem, currentQuantity) {
  const minValue = getMinimumQuantity(product, productItem);
  const maxValue = getMaximumQuantity(product, productItem);

  // Default quantity
  let updatedQuantity = 1;
  if (currentQuantity != null && currentQuantity > 0) {
    // Already has a selected quantity
    updatedQuantity = currentQuantity;
  } else if (minValue > 0) {
    // Has a minimum quantity configured
    updatedQuantity = minValue;
  }

  // Update quantity based on capacity, minimum and maximum quantity
  if (!hasCapacity(product, productItem)) {
    updatedQuantity = 0;
  } else if (updatedQuantity < minValue) {
    updatedQuantity = minValue;
  } else if (updatedQuantity > maxValue && maxValue !== -1) {
    updatedQuantity = maxValue;
  }

  return updatedQuantity;
}

function getMinimumUnitName(product) {
  if (product == null || !isWeightedProduct(product)) {
    return '';
  }

  let minimumUnitName = '';
  if (product.Product != null) {
    minimumUnitName = product.Product.WeightedInfo.MinimumUnitName;
  } else if (product.WeightedInfo != null) {
    minimumUnitName = product.WeightedInfo.MinimumUnitName;
  } else if (product.MinimumUnitName != null) {
    minimumUnitName = product.MinimumUnitName;
  }

  if (minimumUnitName != null) {
    minimumUnitName = minimumUnitName.trim();
  }

  return minimumUnitName;
}

function getStepSize(product) {
  if (product == null) {
    return 1;
  }

  let stepSize = 1;

  if (product.Product != null) {
    stepSize = product.Product.WeightedInfo.StepSize;
  } else if (product.WeightedInfo != null) {
    stepSize = product.WeightedInfo.StepSize;
  } else if (product.StepSize != null) {
    stepSize = product.StepSize;
  }

  if (stepSize === 0) {
    stepSize = 1;
  }

  return stepSize;
}

/**
 * Get correct quantity for visualization purpose.
 * @param {object} product selected product
 * @param {number} quantity current quantity of selected product
 * @returns quantity that user can see
 */
function getDisplayQuantity(product, quantity) {
  if (product == null) {
    return quantity;
  }

  let quantityShown = quantity;
  let stepSize = getStepSize(product);

  if (getMinimumUnitName(product) && stepSize != null && stepSize > 0) {
    quantityShown = quantity / stepSize;
  }

  return quantityShown;
}

/**
 * Get the display unit for Weighted product
 * @param {object} product selected product
 * @returns
 */
function getDisplayUnit(product) {
  if (product == null) {
    return '';
  }

  let minimumUnitName = getMinimumUnitName(product);
  let rawUnit = '';

  if (product.Product != null) {
    rawUnit = product.Product.WeightedInfo.Unit;
  } else if (product.WeightedInfo != null) {
    rawUnit = product.WeightedInfo.Unit;
  } else if (product.Unit != null) {
    rawUnit = product.Unit;
  }

  return minimumUnitName ? minimumUnitName : rawUnit;
}

function getDisplayMinQuantity(product, productItem) {
  const minValue = getMinimumQuantity(product, productItem);
  let stepSize = 1;
  if (getMinimumUnitName(product)) {
    stepSize = getStepSize(product);
  }

  return minValue / stepSize;
}

function getDisplayStepSize(product) {
  let stepSize = 1;
  if (getMinimumUnitName(product)) {
    stepSize = 1;
  } else if (isWeightedProduct(product)) {
    stepSize = getStepSize(product);
  }

  return stepSize;
}

/**
 * Check if a product can be directly added to the order or not without going to
 * the product details page to choose items and options.
 * @param {*} product
 * @returns
 */
function bookableDirectly(product) {
  return (
    product.Type !== 'Rental' &&
    hasOnlyOneItem(product) &&
    !hasOptions(product) &&
    product.Bookable &&
    hasCapacity(product, product.Items[0])
  );
}

export {
  getSortedItemDefNames,
  getSortedItemDefValues,
  removeDefaultItem,
  getItemById,
  getItemByDefs,
  getTotalPrice,
  getTotalRetailPrice,
  getTotalPriceInclVAT,
  getTotalPriceExclVAT,
  getTotalRetailPriceInclVAT,
  getTotalRetailPriceExclVAT,
  getVATText,
  isOnDiscount,
  hasGeneralDiscount,
  personalDiscount,
  applyPersonalDiscount,
  getUnitPrice,
  getRetailUnitPrice,
  getOverviewPrice,
  getRetailOverviewPrice,
  isWeightedProduct,
  getDiscount,
  hasCapacity,
  getCapacity,
  getHighlightingLabelStyle,
  hasOptions,
  toOptionSummary,
  isSameOptionSelections,
  getMinimumQuantity,
  getMaximumQuantity,
  quantityDecreasable,
  quantityIncreasable,
  getQuantity,
  getDisplayQuantity,
  getDisplayUnit,
  getDisplayMinQuantity,
  getDisplayStepSize,
  bookableDirectly,
  getDeposit,
};
